import React from 'react';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { showNotification } from '@mantine/notifications';
import Autoload from '../Autoload';
import RedCross from '../../assets/images/module/general/RedCross.svg';
import { getGermanStatusMessages } from '../../helpers/smokeDetectorHeadStatus';

dayjs.extend(utc);
dayjs.extend(timezone);

const { checkEnvironment, errorHandlingForPDF } = Autoload.MainHelper;

export const sanitizeFileNamePart = (value) => (value || 'ohne-wohneinheit').replace(/[^a-zA-Z0-9-_]+/g, '_');

export const showSmokeDetectorExportError = () => {
  showNotification({
    message: 'Das Protokoll konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    icon: <img src={RedCross} alt="Fehler" />,
  });
};

// Same field breakdown as the PDF template's per-detector row (SmokeDetectorProtocolTemplate.jsx)
// — kept in sync deliberately: both read from getGermanStatusMessages, so a change to message
// wording/severity there updates both exports at once.
const buildDetectorMessages = (detector) => {
  const messages = getGermanStatusMessages(detector.decodedStatus);
  return {
    errorMessages: messages.filter((message) => message.severity === 'error').map((message) => message.message),
    warningMessages: messages.filter((message) => message.severity === 'warning').map((message) => message.message),
    batteryMessage: messages.find((message) => message.field === 'batteryVoltage')?.message,
    dustMessage: messages.find((message) => message.field === 'dustLevel')?.message,
    obstacleMessage: messages.find((message) => message.field === 'obstacleDistance')?.message,
  };
};

// "Ergebnis" column — same OK/Fehler/Warnung summary as the PDF's colored badges, as plain text.
// Excel cell fills aren't available: this project's xlsx package (v0.18.5, the free SheetJS
// build) only supports writing style/fill info as a paid "SheetJS Pro" feature per its own
// README — so there's no Excel equivalent of the PDF's SVG-image badge workaround.
const detectorResultSummary = (detector) => {
  const { hasError, hasWarning, errorFields, warningFields } = detector.severity;
  if (!hasError && !hasWarning) return 'OK';
  const parts = [];
  if (hasError) parts.push(`${errorFields.length} Fehler`);
  if (hasWarning) parts.push(`${warningFields.length} Warnung${warningFields.length > 1 ? 'en' : ''}`);
  return parts.join(', ');
};

// "Details" column — same two-line Fehler/Warnungen breakdown as the PDF, joined with a real
// newline; Excel shows embedded newlines within a cell even without "wrap text" turned on.
const detectorDetailsText = (detector) => {
  const { errorMessages, warningMessages } = buildDetectorMessages(detector);
  return [
    `Fehler: ${errorMessages.length > 0 ? errorMessages.join(', ') : 'keine Fehler'}`,
    `Warnungen: ${warningMessages.length > 0 ? warningMessages.join(', ') : 'keine Warnungen'}`,
  ].join('\n');
};

// "Sonstiger Status" column — same Batterie/Staubpegel/Hindernis breakdown as the PDF.
const detectorOtherStatusText = (detector) => {
  const { batteryMessage, dustMessage, obstacleMessage } = buildDetectorMessages(detector);
  return [`Batterie: ${batteryMessage}`, `Staubpegel: ${dustMessage}`, `Hindernis: ${obstacleMessage}`].join('\n');
};

// Same XLSX.utils.aoa_to_sheet/book_new/book_append_sheet pattern as
// ShowControlSheetForBillingStatementModal.jsx, but writes to an in-memory array instead of
// triggering XLSX.writeFile() directly, so the caller can either download it right away or hand
// it to JSZip for a bulk export. Header info block + column structure mirror
// SmokeDetectorProtocolTemplate.jsx's PDF layout (property/unit/date/method/result rows, then
// Rauchmelder (Nr.)/Bezeichnung/Standort/Ergebnis/Details/Sonstiger Status columns).
export const buildDetectorsExcelBlob = (detectors, { rentalUnitName, readingDateTime, propertySelected } = {}) => {
  // Sum of flagged conditions across all detectors, not the count of detectors that have at
  // least one — same fix as SmokeDetectorDataHandler.buildReadingSummary.
  const errorCount = detectors.reduce((sum, detector) => sum + detector.severity.errorFields.length, 0);
  const warningCount = detectors.reduce((sum, detector) => sum + detector.severity.warningFields.length, 0);
  const readingFormatted = readingDateTime
    ? dayjs.utc(readingDateTime).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format('DD.MM.YYYY (HH:mm)')
    : '-';
  const propertyAddressLine = [
    propertySelected?.propertyAddress,
    [propertySelected?.propertyZipCode, propertySelected?.propertyCity].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  const rows = [
    ['Immobilie:', propertyAddressLine || '-'],
    ['Wohneinheit:', rentalUnitName || 'Ohne Wohneinheit'],
    ['Prüfzeitpunkt:', readingFormatted],
    ['Prüfmethode:', 'Ferninspektion - Verfahren C'],
    ['Ergebnis:', `${detectors.length} Rauchwarnmelder geprüft - ${errorCount} Fehler, ${warningCount} Warnung${warningCount !== 1 ? 'en' : ''}`],
    [],
    ['Rauchmelder (Nr.)', 'Bezeichnung', 'Standort', 'Ergebnis', 'Details', 'Sonstiger Status'],
    ...detectors.map((detector) => [
      detector.meterNo || '-',
      detector.meterName || '-',
      detector.location || '-',
      detectorResultSummary(detector),
      detectorDetailsText(detector),
      detectorOtherStatusText(detector),
    ]),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = [{ wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 50 }, { wch: 40 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Protokoll');
  const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

// Same "Seite X von Y" + centered address footer drawn onto every page as the utility billing
// PDF (ControlSheetPdfTemplate.jsx's mergePdfs) — adapted for a single already-generated PDF
// (that function merges several PDFs into one; here there's always exactly one, so this just
// re-saves it with the same text stamped on).
const stampPdfFooterAndPageNumbers = async (pdfBlob, accountData) => {
  const arrayBuffer = await pdfBlob.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();
  const totalPages = pages.length;

  const footerText = `${accountData?.addressCompany ? accountData.addressCompany + ', ' : ''}${
    accountData?.addressStreet ? accountData.addressStreet + ',' : ''
  } ${accountData?.addressZipCode || ''} ${accountData?.addressCity || ''}`;
  const footerTextWidth = font.widthOfTextAtSize(footerText, 8);
  const grey = rgb(0.5, 0.5, 0.5);

  pages.forEach((page, index) => {
    const { width, height } = page.getSize();
    page.drawText(`Seite ${index + 1} von ${totalPages}`, {
      x: width - 74,
      y: height - 47,
      size: 6,
      color: grey,
      font,
    });
    page.drawText(footerText, {
      x: width / 2 - footerTextWidth / 2,
      y: 30,
      size: 8,
      color: grey,
      font,
    });
  });

  const stampedBytes = await pdfDoc.save();
  return new Blob([stampedBytes], { type: 'application/pdf' });
};

// Clones the already-rendered, off-screen SmokeDetectorProtocolTemplate element and posts it to
// the backend reporting-api/pdf service (same endpoint/technique as
// BulkDownloadForMessageModal.jsx), then stamps the page-number/footer convention on before
// returning the PDF as a Blob rather than triggering a download directly, so both the single-unit
// export button and the bulk/zip export can share this. Cloning (not moving) the live DOM node
// avoids ripping a React-managed element out of the tree; resetting the clone's position avoids
// the backend's headless renderer also honoring the off-screen positioning and producing a blank
// page.
export const buildProtocolPdfBlob = async (element, { authToken, profileData, accountData }) => {
  const clone = element.cloneNode(true);
  clone.style.position = 'static';
  clone.style.left = 'auto';
  clone.style.top = 'auto';
  const doc = document.implementation.createHTMLDocument('Rauchwarnmelder-Protokoll');
  doc.body.appendChild(clone);
  const htmlBlob = new Blob([doc.documentElement.outerHTML], { type: 'text/html' });
  const file = new File([htmlBlob], 'protocol');
  const data = new FormData();
  data.append('file', file);

  const url = `https://${checkEnvironment()}.hellohousing.de/api/reporting-api/pdf`;
  const response = await fetch(url, {
    method: 'POST',
    body: data,
    headers: { Authorization: authToken },
  });

  if (response.status !== 200) {
    errorHandlingForPDF(response, profileData, url);
    throw new Error(`PDF generation failed with ${response.status}`);
  }

  const pdfBlob = await response.blob();
  return stampPdfFooterAndPageNumbers(pdfBlob, accountData);
};
